// Apolo IDE — TaskDagBoard (S2 Task Layer E2-WS1-T01).
//
// Renders a task plan as a horizontal DAG pipeline:
//   - Sequential tasks sit in separate columns (left → right dependency order).
//   - Tasks that canParallelize share a column (branch tracks).
//   - SVG edges connect each task to its successors.
//   - Uses rx-* tokens from S1 (anti-glass, azul Apolo).
//
// Layout algorithm (simple, deterministic):
//   1. Group tasks into "waves": a wave contains all tasks that can run at
//      the same time. Wave 0 = tasks with no predecessors. (For the decomposer,
//      all parallel tasks share wave 0; sequential tasks each get their own wave.)
//   2. Render each wave as a column. Tasks in a wave stack vertically.
//   3. Edges connect every task in wave N to every task in wave N+1.
//
// This is intentionally lightweight (no dagre/elk dependency). The decomposer
// output is small (≤ 6 tasks) so a manual layout is sufficient.

import { useMemo, useRef, useCallback } from 'react';
import { TaskNode } from './TaskNode.jsx';
import { waves as graphWaves } from '../../../lib/taskGraph.js';

// ── Layout constants ────────────────────────────────────────────────────────
const NODE_W = 180;
const NODE_H = 110;
const COL_GAP = 60;  // horizontal gap between columns
const ROW_GAP = 14;  // vertical gap between nodes in the same column
const PAD_X = 16;
const PAD_Y = 16;

/**
 * Compute column (wave) assignments.
 * Tasks with canParallelize=true all land in the same first column.
 * Sequential tasks (canParallelize=false) each get their own column AFTER
 * the parallel batch.
 *
 * @param {import('../../../lib/taskTypes.js').Task[]} tasks
 * @returns {Array<import('../../../lib/taskTypes.js').Task[]>}  columns[colIndex] = tasks[]
 */
function computeColumns(tasks) {
  if (!tasks || tasks.length === 0) return [];

  // S2 — DAG REAL: se as tasks têm depends_on (o decomposer agora emite), as
  // colunas saem das WAVES do taskGraph (ordem por dependência verdadeira), não
  // mais do canParallelize chato. Fallback pro legado quando não há deps.
  const hasDeps = tasks.some((t) => Array.isArray(t.depends_on) && t.depends_on.length > 0);
  if (hasDeps) {
    const w = graphWaves(tasks);
    if (w && w.length) return w;
  }

  const parallel = tasks.filter((t) => t.canParallelize);
  const sequential = tasks.filter((t) => !t.canParallelize);

  const cols = [];
  if (parallel.length > 0) cols.push(parallel);
  for (const t of sequential) cols.push([t]);
  if (cols.length === 0) cols.push(tasks); // fallback: all in one column
  return cols;
}

/**
 * Compute pixel positions for each task node.
 * @returns {{ positions: Map<string, {x:number,y:number}>, totalW: number, totalH: number }}
 */
function computeLayout(cols) {
  const positions = new Map();
  let totalW = PAD_X;
  let totalH = 0;

  for (let ci = 0; ci < cols.length; ci += 1) {
    const col = cols[ci];
    const colH = col.length * NODE_H + (col.length - 1) * ROW_GAP;
    let y = PAD_Y;
    for (const task of col) {
      positions.set(task.id, { x: totalW, y });
      y += NODE_H + ROW_GAP;
    }
    totalW += NODE_W + (ci < cols.length - 1 ? COL_GAP : 0);
    totalH = Math.max(totalH, colH + PAD_Y * 2);
  }
  totalW += PAD_X;
  return { positions, totalW, totalH };
}

/**
 * Build edges (from: taskId, to: taskId) connecting consecutive columns.
 */
function buildEdges(cols, positions) {
  const edges = [];
  for (let ci = 0; ci < cols.length - 1; ci += 1) {
    for (const from of cols[ci]) {
      for (const to of cols[ci + 1]) {
        const fp = positions.get(from.id);
        const tp = positions.get(to.id);
        if (!fp || !tp) continue;
        edges.push({
          id: `${from.id}→${to.id}`,
          x1: fp.x + NODE_W,       // right edge of from-node
          y1: fp.y + NODE_H / 2,   // vertical center
          x2: tp.x,                 // left edge of to-node
          y2: tp.y + NODE_H / 2,
        });
      }
    }
  }
  return edges;
}

/**
 * @param {{
 *   tasks: import('../../../lib/taskTypes.js').Task[]
 *   verifyResults?: Map<string, object>
 *   selectedTaskId?: string | null
 *   onTaskClick?: (task: import('../../../lib/taskTypes.js').Task) => void
 *   className?: string
 *   style?: React.CSSProperties
 * }} props
 */
export function TaskDagBoard({ tasks = [], verifyResults, selectedTaskId, onTaskClick, className, style }) {
  const containerRef = useRef(null);

  const { positions, edges, totalW, totalH } = useMemo(() => {
    const cols = computeColumns(tasks);
    const { positions, totalW, totalH } = computeLayout(cols);
    const edges = buildEdges(cols, positions);
    return { positions, edges, totalW, totalH };
  }, [tasks]);

  const handleTaskClick = useCallback(
    (task) => { onTaskClick?.(task); },
    [onTaskClick],
  );

  if (!tasks || tasks.length === 0) {
    return (
      <div
        data-testid="task-dag-board-empty"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          color: 'var(--rx-tx3)',
          fontSize: 12,
          ...style,
        }}
        className={className}
      >
        Nenhuma task no plano.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="task-dag-board"
      aria-label={`DAG de ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`}
      style={{
        position: 'relative',
        overflowX: 'auto',
        overflowY: 'visible',
        ...style,
      }}
      className={className}
    >
      <svg
        width={totalW}
        height={totalH}
        aria-hidden="true"
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* Edges — drawn first so nodes render on top */}
        {edges.map((e) => {
          const mx = (e.x1 + e.x2) / 2;
          return (
            <path
              key={e.id}
              d={`M${e.x1},${e.y1} C${mx},${e.y1} ${mx},${e.y2} ${e.x2},${e.y2}`}
              stroke="var(--rx-border)"
              strokeWidth={1.5}
              fill="none"
              markerEnd="url(#task-dag-arrow)"
            />
          );
        })}

        {/* Arrow marker */}
        <defs>
          <marker id="task-dag-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0, 7 3.5, 0 7" fill="var(--rx-border)" />
          </marker>
        </defs>

        {/* Task nodes via foreignObject */}
        {tasks.map((task) => {
          const pos = positions.get(task.id);
          if (!pos) return null;
          return (
            <foreignObject
              key={task.id}
              x={pos.x}
              y={pos.y}
              width={NODE_W}
              height={NODE_H}
              overflow="visible"
            >
              <TaskNode
                task={task}
                verifyResult={verifyResults?.get(task.id)}
                selected={selectedTaskId === task.id}
                onClick={() => handleTaskClick(task)}
              />
            </foreignObject>
          );
        })}
      </svg>
    </div>
  );
}
